import React, { useState, useEffect } from 'react';
import { toDateInputValue } from '../utils/dateInput';
import { X } from 'react-feather';
import { tournamentAPI, TournamentGame } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useResponsive } from '../hooks/useResponsive';
import Modal from './Modal';
import { TextInput, TextArea } from './ui';

interface AddEditGameModalProps {
  tournamentId: string;
  game: TournamentGame | null;
  onClose: () => void;
  onSuccess: () => Promise<void>;
}

const AddEditGameModal: React.FC<AddEditGameModalProps> = ({
  tournamentId,
  game,
  onClose,
  onSuccess,
}) => {
  const { sessionToken } = useAuth();
  const toast = useToast();
  const { isMobile } = useResponsive();
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    game_date: '',
    game_time: '',
    opponent: '',
    field_number: '',
    notes: '',
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    if (game) {
      setFormData({
        game_date: toDateInputValue(game.game_date),
        game_time: game.game_time || '',
        opponent: game.opponent || '',
        field_number: game.field_number || '',
        notes: game.notes || '',
      });
    }
  }, [game]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validate = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.game_date) {
      newErrors.game_date = 'Game date is required';
    }

    if (!formData.opponent.trim()) {
      newErrors.opponent = 'Opponent name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    if (!sessionToken) {
      toast.error('You must be logged in');
      return;
    }

    setSubmitting(true);

    try {
      const gameData = {
        tournament_id: tournamentId,
        game_date: formData.game_date,
        game_time: formData.game_time.trim() || undefined,
        opponent: formData.opponent.trim(),
        field_number: formData.field_number.trim() || undefined,
        notes: formData.notes.trim() || undefined,
      };

      let response;
      if (game) {
        response = await tournamentAPI.updateGame(sessionToken, game.id, gameData);
      } else {
        response = await tournamentAPI.addGame(sessionToken, tournamentId, gameData);
      }

      if (response.success) {
        toast.success(game ? 'Game updated successfully' : 'Game added successfully');
        await onSuccess();
        onClose();
      } else {
        // Don't silently no-op on success:false (audit #196).
        toast.error(`Failed to ${game ? 'update' : 'add'} game`);
      }
    } catch (error: any) {
      console.error('Submit error:', error);
      toast.error(error.message || `Failed to ${game ? 'update' : 'add'} game`);
    } finally {
      setSubmitting(false);
    }
  };

  // Dirty tracking for the shared Modal's discard guard (audit #223).
  const isDirty = game
    ? (
        formData.game_date !== (game.game_date || '') ||
        formData.game_time !== (game.game_time || '') ||
        formData.opponent !== (game.opponent || '') ||
        formData.field_number !== (game.field_number || '') ||
        formData.notes !== (game.notes || '')
      )
    : (
        formData.game_date !== '' ||
        formData.game_time !== '' ||
        formData.opponent.trim() !== '' ||
        formData.field_number.trim() !== '' ||
        formData.notes.trim() !== ''
      );

  return (
    <Modal
      isOpen
      onClose={onClose}
      isDirty={isDirty}
      labelledById="game-modal-title"
      zIndex={1003}
      contentStyle={{
        maxWidth: isMobile ? 'calc(100vw - 32px)' : '500px',
        maxHeight: isMobile ? 'calc(100vh - 100px)' : '90vh',
        width: isMobile ? 'calc(100vw - 32px)' : '100%',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
      }}
    >
        {/* Header */}
        <div style={{
          ...styles.header,
          padding: isMobile ? '16px' : '24px',
        }}>
          <h2 id="game-modal-title" style={styles.title}>{game ? 'Edit Game' : 'Add Game'}</h2>
          <button onClick={onClose} aria-label="Close" style={styles.closeButton}>
            <X size={24} aria-hidden="true" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.formContent}>
            {/* Every label in here pointed at nothing — no htmlFor, no id — so
                clicking one did not focus its box and a screen reader read the
                fields unnamed. The ids are the fix; the primitives bring the
                focus, invalid and disabled states with them. */}
            {/* Opponent */}
            <div style={styles.formGroup}>
              <label htmlFor="game-opponent" style={styles.label}>
                Opponent <span style={styles.required}>*</span>
              </label>
              <TextInput
                id="game-opponent"
                type="text"
                name="opponent"
                value={formData.opponent}
                onChange={handleChange}
                invalid={!!errors.opponent}
                aria-describedby={errors.opponent ? 'game-opponent-error' : undefined}
                style={{
                  ...styles.input,
                  ...(errors.opponent ? styles.inputError : {}),
                }}
                placeholder="e.g., Georgia Thunder"
              />
              {errors.opponent && <span id="game-opponent-error" role="alert" style={styles.errorText}>{errors.opponent}</span>}
            </div>

            {/* Date and Time - Grid */}
            <div style={{...styles.formRow, gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr'}}>
              <div style={styles.formGroup}>
                <label htmlFor="game-date" style={styles.label}>
                  Game Date <span style={styles.required}>*</span>
                </label>
                <TextInput
                  id="game-date"
                  type="date"
                  name="game_date"
                  value={formData.game_date}
                  onChange={handleChange}
                  invalid={!!errors.game_date}
                  aria-describedby={errors.game_date ? 'game-date-error' : undefined}
                  style={{
                    ...styles.input,
                    ...(errors.game_date ? styles.inputError : {}),
                  }}
                />
                {errors.game_date && (
                  <span id="game-date-error" role="alert" style={styles.errorText}>{errors.game_date}</span>
                )}
              </div>

              <div style={styles.formGroup}>
                <label htmlFor="game-time" style={styles.label}>Game Time</label>
                <TextInput
                  id="game-time"
                  type="time"
                  name="game_time"
                  value={formData.game_time}
                  onChange={handleChange}
                  style={styles.input}
                />
              </div>
            </div>

            {/* Field Number */}
            <div style={styles.formGroup}>
              <label htmlFor="game-field-number" style={styles.label}>Field Number</label>
              <TextInput
                id="game-field-number"
                type="text"
                name="field_number"
                value={formData.field_number}
                onChange={handleChange}
                style={styles.input}
                placeholder="e.g., Field 3"
              />
            </div>

            {/* Notes */}
            <div style={styles.formGroup}>
              <label htmlFor="game-notes" style={styles.label}>Notes</label>
              <TextArea
                id="game-notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                style={styles.textarea}
                placeholder="Add any additional information..."
                rows={3}
              />
            </div>
          </div>

          {/* Footer */}
          <div style={styles.footer}>
            <button type="button" onClick={onClose} style={styles.cancelButton}>
              Cancel
            </button>
            <button type="submit" disabled={submitting} style={styles.submitButton}>
              {submitting ? 'Saving...' : game ? 'Save Changes' : 'Add Game'}
            </button>
          </div>
        </form>
    </Modal>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1003,
    padding: '20px',
  },
  modal: {
    backgroundColor: '#FFFFFF',
    borderRadius: '16px',
    width: '100%',
    maxWidth: '500px',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '24px',
    borderBottom: '1px solid #E5E7EB',
  },
  title: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#1F2937',
    margin: 0,
    fontFamily: 'Manrope, sans-serif',
  },
  closeButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '8px',
    color: '#6B7280',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '8px',
    transition: 'all 0.2s',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
  },
  formContent: {
    flex: 1,
    overflowY: 'auto',
    padding: '24px',
  },
  formGroup: {
    marginBottom: '20px',
    display: 'flex',
    flexDirection: 'column',
  },
  formRow: {
    display: 'grid',
    gap: '16px',
    marginBottom: '20px',
  },
  label: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: '8px',
    fontFamily: 'Manrope, sans-serif',
  },
  required: {
    color: '#EF4444',
  },
  input: {
    width: '100%',
    maxWidth: '100%',
    padding: '10px 12px',
    fontSize: '14px',
    border: '1px solid #D1D5DB',
    borderRadius: '8px',
    fontFamily: 'Manrope, sans-serif',
    transition: 'border-color 0.2s',
    boxSizing: 'border-box' as const,
    WebkitAppearance: 'none' as const,
    MozAppearance: 'none' as const,
    appearance: 'none' as const,
  },
  textarea: {
    padding: '10px 12px',
    fontSize: '14px',
    border: '1px solid #D1D5DB',
    borderRadius: '8px',
    fontFamily: 'Manrope, sans-serif',
    resize: 'vertical',
    transition: 'border-color 0.2s',
  },
  inputError: {
    borderColor: '#EF4444',
  },
  errorText: {
    fontSize: '12px',
    color: '#EF4444',
    marginTop: '4px',
    fontFamily: 'Manrope, sans-serif',
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    padding: '16px 24px',
    borderTop: '1px solid #E5E7EB',
  },
  cancelButton: {
    padding: '10px 20px',
    backgroundColor: '#FFFFFF',
    border: '1px solid #D1D5DB',
    borderRadius: '8px',
    color: '#6B7280',
    fontSize: '14px',
    fontWeight: '600',
    fontFamily: 'Manrope, sans-serif',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  submitButton: {
    padding: '10px 20px',
    backgroundColor: '#F9550D',
    border: 'none',
    borderRadius: '8px',
    color: '#FFFFFF',
    fontSize: '14px',
    fontWeight: '600',
    fontFamily: 'Manrope, sans-serif',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
};

export default AddEditGameModal;
